/**
 * Service Worker registration and management
 * Uses Workbox via Vite PWA plugin for offline capability
 */

import { registerSW } from 'virtual:pwa-register';

let updateSW = null;

/**
 * Register the service worker for offline capability
 * @returns {Promise<void>}
 */
export async function registerServiceWorker() {
  // Skip service worker registration in development to prevent fetch interference
  if (import.meta.env.DEV) {
    console.log('⏭️ Skipping service worker registration in development mode');
    // Also unregister any existing service workers from previous sessions
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('🧹 Unregistered stale dev service worker');
      }
    }
    return;
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported in this browser');
    return;
  }

  try {
    // Register service worker with auto-update
    updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        console.log('🔄 New content available, please refresh.');
        showUpdateNotification();
      },
      onOfflineReady() {
        console.log('✅ App ready to work offline');
        showOfflineReadyNotification();
      },
      onRegistered(registration) {
        console.log('✅ Service Worker registered:', registration);

        // Check for updates every hour
        if (registration) {
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000); // 1 hour
        }
      },
      onRegisterError(error) {
        console.error('❌ Service Worker registration failed:', error);
      }
    });

    console.log('✅ PWA service worker registered successfully');
  } catch (error) {
    console.error('❌ Service worker registration failed:', error);
    throw error;
  }
}

/**
 * Show notification when new content is available
 */
function showUpdateNotification() {
  // Create update notification
  const notification = document.createElement('div');
  notification.id = 'sw-update-notification';
  notification.innerHTML = `
    <div style="
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #333;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 16px;
      max-width: 90%;
    ">
      <span>🔄 Nuwe weergawe beskikbaar!</span>
      <button id="sw-update-btn" style="
        background: #4CAF50;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
      ">
        Werk Op
      </button>
      <button id="sw-dismiss-btn" style="
        background: transparent;
        color: white;
        border: 1px solid white;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
      ">
        Later
      </button>
    </div>
  `;

  document.body.appendChild(notification);

  // Update button handler
  document.getElementById('sw-update-btn')?.addEventListener('click', () => {
    if (updateSW) {
      updateSW(true); // Force update
    } else {
      window.location.reload();
    }
  });

  // Dismiss button handler
  document.getElementById('sw-dismiss-btn')?.addEventListener('click', () => {
    notification.remove();
  });
}

/**
 * Show notification when app is ready to work offline
 */
function showOfflineReadyNotification() {
  const notification = document.createElement('div');
  notification.id = 'sw-offline-notification';
  notification.innerHTML = `
    <div style="
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #4CAF50;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      animation: slideUp 0.3s ease-out;
    ">
      ✅ App is gereed om vanlyn te werk!
    </div>
    <style>
      @keyframes slideUp {
        from {
          transform: translateX(-50%) translateY(100px);
          opacity: 0;
        }
        to {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }
      }
    </style>
  `;

  document.body.appendChild(notification);

  // Auto-dismiss after 3 seconds
  setTimeout(() => {
    notification.style.animation = 'slideUp 0.3s ease-out reverse';
    setTimeout(() => notification.remove(), 300);
  }, 3000);
}

/**
 * Check if the app is currently online
 * @returns {boolean}
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Add online/offline event listeners
 * @param {Function} onOnline - Callback when going online
 * @param {Function} onOffline - Callback when going offline
 */
export function addConnectivityListeners(onOnline, onOffline) {
  window.addEventListener('online', () => {
    console.log('🌐 Connection restored');
    if (onOnline) onOnline();
  });

  window.addEventListener('offline', () => {
    console.log('📡 Connection lost - working offline');
    if (onOffline) onOffline();
  });
}

/**
 * Unregister the service worker (for development/testing)
 */
export async function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
      console.log('Service worker unregistered');
    }
  }
}
